/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  CardListSkeleton,
  ChevronLeftIcon,
  DataState,
  DataStateError,
  Text,
  UnstyledButton,
} from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { EntityDocumentsPanel } from '../components/EntityDocumentsPanel';
import { SaleActionRow } from '../components/SaleActionRow';
import { SaleActivityFeed } from '../components/SaleActivityFeed';
import { SaleCommissionSection } from '../components/SaleCommissionSection';
import { SaleDetailHeader, SaleStatusBand } from '../components/SaleDetailHeader';
import { SaleKpiCards } from '../components/SaleKpiCards';
import { SaleRecordCard } from '../components/SaleRecordCard';
import { SaleOutcomeCard } from '../components/SaleOutcomeCard';
import { SalePaperworkPanel } from '../components/SalePaperworkPanel';
import { SalePartiesPanel } from '../components/SalePartiesPanel';
import { Pill } from '../components/sale-detail-atoms';
import {
  BUILT_IN_DOCUMENT_CATEGORY_IDS,
  SALE_DOCUMENT_CATEGORIES,
  canRecordCommission,
  isOpen,
  isTerminal,
} from '../constants/sale-states';
import { useSale } from '../hooks/useSale';
import { useSalePaperworkMilestoneTypes } from '../hooks/useSalePaperworkMilestoneTypes';
import { useSaleSettings } from '../hooks/useSaleSettings';
import { useSaleProperty } from '../hooks/useSaleProperty';
import { SaleService } from '../services/sale-service';
import type { Sale, SaleDocument } from '../types/sale.types';

const TABS = ['paperwork', 'commission', 'documents', 'parties'] as const;
type SaleTab = (typeof TABS)[number];

/**
 * The sale record: what was sold and to whom, how far the paperwork has got,
 * what the commission has done, and what may still be done to it.
 *
 * Laid out on the same recipe as the approved attendance and lease
 * prototypes — identity header, four figures, then a wide working column
 * beside a narrow activity column. The layout is state-driven: a sale still
 * in paperwork gets the action row, and a terminal one gets the outcome card
 * in its place with every form switched to read-only, because both terminal
 * states are final by design.
 *
 * @returns The sale detail page.
 */
export function SaleDetailPage() {
  const { agency_id: agencyId = '', sale_id: saleId = '' } = useParams<{
    agency_id: string;
    sale_id: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation('features/sales');
  const [activeTab, setActiveTab] = useState<SaleTab>('paperwork');

  const {
    isLoading, isError, sale, commission, documents,
    applyTransition, recordPaperwork, recordReceipt, refresh,
  } = useSale(agencyId, saleId);

  const { types: milestoneTypes } = useSalePaperworkMilestoneTypes(agencyId);
  // The vocabulary is the agency's own; the seven built-in ids stay
  // translated by i18n, and only the ones it added carry their own label.
  const { documentCategories } = useSaleSettings(agencyId);
  const documentCategoryIds = useMemo(
    () => documentCategories.map((c) => c.id),
    [documentCategories],
  );
  const documentCategoryLabels = useMemo(
    () => Object.fromEntries(
      documentCategories
        .filter((c) => !BUILT_IN_DOCUMENT_CATEGORY_IDS.has(c.id))
        .map((c) => [c.id, c.label]),
    ),
    [documentCategories],
  );
  const property = useSaleProperty(agencyId, sale?.property_id);

  async function uploadDocument(file: File, category: string, label?: string) {
    const link = await SaleService.createDocumentUploadLink(agencyId, saleId, {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      category,
      ...(label ? { label } : {}),
    });
    const form = new FormData();
    Object.entries(link.upload_url.fields ?? {}).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);
    await fetch(link.upload_url.url, { method: 'POST', body: form });
    refresh();
  }

  async function openDocument(documentId: string) {
    const { url } = await SaleService.getDocumentViewLink(agencyId, saleId, documentId);
    window.open(url, '_blank', 'noopener');
  }

  async function deleteDocument(documentId: string) {
    await SaleService.deleteDocument(agencyId, saleId, documentId);
    refresh();
    toast.success(t('documents.removed'));
  }

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col overflow-y-auto',
        'bg-(--surface-secondary) p-(--space-5)',
        'font-[family-name:var(--font-family)]',
      )}
    >
      <UnstyledButton
        onClick={() => navigate(`/app/agencies/${agencyId}/sales`)}
        className="mb-(--space-3) flex w-fit cursor-pointer items-center gap-(--space-1) text-(--text-secondary) hover:text-(--text-primary)"
        data-testid="sale-breadcrumb-back"
      >
        <ChevronLeftIcon />
        <Text size="sm">{t('pageTitle')}</Text>
      </UnstyledButton>

      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={false}
        loadingFallback={<CardListSkeleton count={4} />}
        errorState={<DataStateError message={t('detail.loadError')} onRetry={refresh} />}
      >
        {sale && (
          <div className="flex grow flex-col gap-(--space-4)">
            <SaleDetailHeader
              sale={sale}
              propertyLabel={property.label}
              propertyLocality={property.locality}
              onOpenOriginDeal={() =>
                navigate(`/app/agencies/${agencyId}/deals/${sale.origin_deal_id}`)
              }
              band={
                <SaleStatusBand
                  sale={sale}
                  milestoneTypes={milestoneTypes}
                  pending={commission?.paperwork_pending}
                  isLoading={commission == null || milestoneTypes.length === 0}
                  actions={
                    isOpen(sale.state) ? (
                      <SaleActionRow
                        onApply={applyTransition}
                        onGoToTab={(tab) => setActiveTab(tab)}
                      />
                    ) : undefined
                  }
                />
              }
            />

            <SaleKpiCards sale={sale} commission={commission} />

            <div
              className={cn(
                'grid grow items-start gap-(--space-4) lg:items-stretch',
                'grid-cols-1 lg:[grid-template-columns:minmax(0,1.6fr)_minmax(0,1fr)]',
              )}
            >
              <div className="flex min-w-0 flex-col gap-(--space-4) [&>*:last-child]:grow [&>*:last-child]:shrink-0">
                {isTerminal(sale.state) && <SaleOutcomeCard sale={sale} />}

                <div
                  role="tablist"
                  className="flex flex-nowrap gap-0 px-(--space-3) overflow-x-auto border-b border-(--border-subtle) scrollbar-none"
                >
                  {TABS.map((tab) => (
                    <UnstyledButton
                      key={tab}
                      role="tab"
                      aria-selected={activeTab === tab}
                      data-testid={`sale-tab-${tab}`}
                      onClick={() => setActiveTab(tab)}
                      className={cn(
                        'flex cursor-pointer items-center gap-(--space-1-5) whitespace-nowrap',
                        // Matches the property, client, lease and attendance
                        // tabs: one straight rule across the strip, and an
                        // active tab in seed rather than ink.
                        'rounded-none border-b-2 px-(--space-5) py-(--space-3) text-(length:--text-sm)',
                        activeTab === tab
                          ? 'border-b-(--seed-base) font-semibold text-(--seed-base)'
                          : 'border-b-transparent font-medium text-(--text-secondary)',
                      )}
                    >
                      <Text component="span">{t(`detail.tabs.${tab}`)}</Text>
                      <Pill accent={activeTab === tab ? 'var(--seed-base)' : undefined}>
                        {tabCount(tab, sale, documents)}
                      </Pill>
                    </UnstyledButton>
                  ))}
                </div>

                {activeTab === 'paperwork' && (
                  <SalePaperworkPanel
                    sale={sale}
                    milestoneTypes={milestoneTypes}
                    onRecordPaperwork={recordPaperwork}
                    readOnly={!isOpen(sale.state)}
                  />
                )}

                {activeTab === 'commission' && (
                  <SaleCommissionSection
                    panel={commission}
                    receipts={sale.commission_receipts ?? []}
                    onRecord={recordReceipt}
                    readOnly={!canRecordCommission(sale.state)}
                  />
                )}

                {activeTab === 'documents' && (
                  <EntityDocumentsPanel
                    documents={documents}
                    categories={
                      documentCategoryIds.length ? documentCategoryIds : SALE_DOCUMENT_CATEGORIES
                    }
                    categoryLabels={documentCategoryLabels}
                    namespace="features/sales"
                    onUpload={uploadDocument}
                    onOpen={openDocument}
                    onDelete={deleteDocument}
                    onRetry={refresh}
                  />
                )}

                {activeTab === 'parties' && (
                  <SalePartiesPanel
                    buyerName={sale.buyer_name}
                    sellers={sale.sellers}
                  />
                )}
              </div>

              <div className="flex flex-col gap-(--space-4) lg:sticky lg:top-(--space-5)">
                <SaleRecordCard sale={sale} />
                <SaleActivityFeed sale={sale} milestoneTypes={milestoneTypes} />
              </div>
            </div>
          </div>
        )}
      </DataState>
    </div>
  );
}

/**
 * How many items a tab holds, shown in its pill.
 *
 * @param tab - The tab being labelled.
 * @param sale - The sale record.
 * @param documents - The documents currently loaded.
 * @returns The count to display.
 */
function tabCount(tab: SaleTab, sale: Sale, documents: SaleDocument[]): number {
  if (tab === 'paperwork') {
    return Object.keys(sale.paperwork).length;
  }
  if (tab === 'commission') return (sale.commission_receipts ?? []).length;
  if (tab === 'documents') return documents.length;
  return (sale.buyer_client_id ? 1 : 0) + (sale.sellers ?? []).length;
}

export default SaleDetailPage;
